'use client';

import { useContext, useEffect } from 'react';

import createStyles from '@guestyci/foundation/createStyles';

import { WebsiteSettingsContext } from 'context/WebsiteSettingsContext';
import { getDocument } from 'utils/isBrowser';

const MAIN_COLOR = '#2053f8';
const useStyles = createStyles(() => {
  return {
    '@global': {
      'body > div.arc-scope': {
        '& button[data-range-start="true"], & button[data-range-end="true"], & button[data-selected-single="true"], & button[data-state="checked"]': {
          borderColor: `var(--btn-color, ${MAIN_COLOR})`,
          backgroundColor: `var(--btn-color, ${MAIN_COLOR})`,
        },
        '& .gst-ring-offset-2:focus-within': {
          '--tw-ring-color': `var(--btn-color, ${MAIN_COLOR})`,
        },
      }
    },
    root: {
      background: '#fff',
      height: '100%',
      width: '100%',
      '& .date-picker .DateInput_input': {
        padding: '0px 5px!important',
      },
      '& .radio': {
        '& div[class*="checked"]': {
          borderColor: ({ btnColor }) => btnColor || '#1BBCEF',
        },
        '& .radio-inner-content': {
          backgroundColor: ({ btnColor }) => btnColor || '#1BBCEF',
        },
      },
      '& .range': {
        accentColor: ({ btnColor }) => btnColor || '#1BBCEF',
        '&[type="range"]': {
          borderRadius: 5,
          height: 5,
          outline: 'none',
          appearance: 'none',
          transition: 'background 450ms ease-in',
        },
      },
      '& .btn': {
        padding: '10px 30px',
        height: 46,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: '#fff',
        cursor: 'pointer',
        border: 0,
        boxShadow: 'none',
        '&:disabled': {
          background: '#BDBDBD',
          color: '#fff',
        },
        '&-full': {
          width: ['100%', '!important'],
        },
        '&-colored': {
          background: ({ btnColor }) => btnColor || MAIN_COLOR,
        },
        '&-outline': {
          background: '#fff!important',
          border: ({ btnColor }) => `1px solid ${btnColor || MAIN_COLOR}`,
          color: ({ btnColor }) => btnColor || MAIN_COLOR,
          '&:hover': {
            background: ({ btnColor }) => btnColor || MAIN_COLOR,
            color: ({ btnColor }) => btnColor,
          },
        },
        '&-plain': {
          color: ({ btnColor }) => btnColor,
        },
        '&-sm': {
          width: 'auto',
          height: 46,
          padding: '10px 20px',
        },
        '&:hover': {
          textDecoration: 'none',
          color: '#fff',
        },
      },
      '& a': {
        color: ({ btnColor }) => btnColor || MAIN_COLOR,
      },
    },
  };
});

const RootStylesWrapper = ({ children }) => {
  const { contentConfiguration: { colorTheme } = {} } = useContext(WebsiteSettingsContext);
  const btnColor = colorTheme?.colors?.mainButtonsAndLinks;
  const { root } = useStyles({ btnColor });
  const doc = getDocument();

  // Set CSS variable on body for portaled elements
  //   e.g. dialogs and comboboxes that are appended to the body
  useEffect(() => {
    if (btnColor) {
      doc.body.style.setProperty('--btn-color', btnColor);
    }

    return () => doc.body.style.removeProperty('--btn-color');
  }, [btnColor, doc]);

  return (
    <div className={root}>
      {children}
      {/*
        Safari fix.
        It is probably has to do with tabindex; like it looks like Safari doesn't like when you close the popup and the browser doesn't have any other input / "tabindexable" element to focus.
        Explanation: https://github.com/algolia/docsearch/issues/1260#issuecomment-1011939736
      */}
      <div style={{ position: 'fixed', zIndex: -1, opacity: 0 }}>
        <input type="text" aria-hidden="true" tabIndex="-1" aria-label="Safari focus management fix" />
      </div>
    </div>
  );
};

export default RootStylesWrapper;
